import { QueryClientProvider } from '@tanstack/react-query';
import { Locale } from '@tix-ticketing/ui-theme';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { IntlProvider } from 'react-intl';
import { allLocales } from 'messages';
import type { LocaleKey } from 'messages';
import type { HeadersConfig } from '../pages/_app';
import { useAppState } from './app-state';
import { defaultRichTextElements } from './default-rich-text-elements';
import { useSessionIdentitySync } from './hooks/use-session-identity-sync';
import { awaitPolyfills } from './polyfills';
import { initApiClient, queryClient, updateRequestHeaders } from './tanstack-query-client';
type Props = {
  headersConfig: HeadersConfig;
  children: ReactNode;
};

// Init guard is client-only. The axios client is a module-level singleton,
// and on the server it's shared across concurrent requests — we don't want
// the first request's headers leaking into subsequent renders. SSR doesn't
// fire queries (React Query is inert during render), so the singleton never
// needs to be configured server-side.
let apiClientInitialized = false;
export const AppProviders: FC<Props> = props => {
  const appState = useAppState();
  const prevLocaleRef = useRef(appState.locale);
  if (typeof window !== 'undefined' && !apiClientInitialized) {
    initApiClient(props.headersConfig.headers);
    apiClientInitialized = true;
  }
  useSessionIdentitySync();
  useEffect(() => {
    initApiClient(props.headersConfig.headers);
    // Headers are set in an effect, never during render: `client` is a module
    // singleton, and mutating it mid-render would let a re-render overwrite a
    // token the 401 interceptor had just refreshed.
    // `appState.tokens` is in the dependency list below as the trigger: the
    // value itself comes from the token store, so a refresh made outside React
    // wins over whatever this render happens to hold.
    updateRequestHeaders(appState.locale);

    // Refetch all queries when the locale changes so translated data is updated
    if (prevLocaleRef.current !== appState.locale) {
      prevLocaleRef.current = appState.locale;
      queryClient.invalidateQueries();
    }
  }, [appState.locale, appState.tokens, props.headersConfig]);
  return <IntlProvider messages={appState.messages} locale={appState.locale} timeZone={appState.appSettings?.timezone ?? 'UTC'} defaultLocale={Locale.En} defaultRichTextElements={defaultRichTextElements} onError={() => void 0}>
      <QueryClientProvider client={queryClient}>
        <AppSettingsLoader />
        {props.children}
      </QueryClientProvider>
    </IntlProvider>;
};

/**
 * Resolves an API locale (LanguageDTO.culture like "is-IS" or
 * twoLetterISOCulture like "is") to a compiled LocaleKey, or null if
 * there is no matching compiled translation.
 */
function resolveLocaleKey(culture: string | null | undefined, twoLetter: string | null | undefined): LocaleKey | null {
  const keys = Object.keys(allLocales) as LocaleKey[];

  // Try exact match on culture first (e.g. "is-IS" → "is-IS")
  if (culture) {
    const match = keys.find(k => k.toLowerCase() === culture.toLowerCase());
    if (match) return match;
  }

  // Fallback: match by two-letter prefix (e.g. "is" → "is-IS")
  if (twoLetter) {
    const match = keys.find(k => k.split('-')[0]?.toLowerCase() === twoLetter.toLowerCase());
    if (match) return match;
  }
  return null;
}

/**
 * App settings arrive via _app.getInitialProps -> AppStateProvider. This
 * component just validates persisted locale/currency against the
 * server-provided availableLocales/Currencies and gates rendering on
 * polyfills.
 */
function AppSettingsLoader() {
  const appState = useAppState();
  useEffect(() => {
    const data = appState.appSettings;
    if (data) {
      if (data.availableLocales?.length) {
        const currentLocaleAvailable = data.availableLocales.some(l => resolveLocaleKey(l.culture, l.twoLetterISOCulture) === appState.locale);
        if (!currentLocaleAvailable && data.defaultLocale) {
          const fallback = resolveLocaleKey(data.defaultLocale.culture, data.defaultLocale.twoLetterISOCulture);
          if (fallback) {
            appState.setLocale(fallback);
          }
        }
      }
      if (data.availableCurrencies?.length) {
        const persistedAvailable = data.availableCurrencies.some(c => c.currencyKey === appState.currency);
        if (!persistedAvailable && data.defaultCurrency?.currencyKey) {
          appState.setCurrency(data.defaultCurrency.currencyKey);
        }
      }
    }

    // Wait for Intl polyfills before rendering. polyfill-force replaces
    // native Intl — locale data must be loaded before any date/number
    // formatting happens.
    awaitPolyfills().finally(() => {
      appState.setAppSettingsReady(true);
    });
  }, []);
  return null;
}